import React, { useState, useEffect } from 'react';
import { useSettings } from '../../hooks/useSettings';
import { NotificationsSection } from '../NotificationsSection';
import { ArrowLeftIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

/**
 * SettingsView Component
 * Main settings page UI - shared across all roles
 */
export const SettingsView: React.FC = () => {
  const navigate = useNavigate();
  const { settings, isLoading, isUpdating, updateSettings } = useSettings();

  // Local state for sound notifications toggle
  const [soundNotificationsEnabled, setSoundNotificationsEnabled] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local state when settings load
  useEffect(() => {
    if (settings) {
      setSoundNotificationsEnabled(settings.soundNotificationsEnabled);
    }
  }, [settings]);

  // Detect changes
  useEffect(() => {
    if (settings) {
      const changed = soundNotificationsEnabled !== settings.soundNotificationsEnabled;
      setHasChanges(changed);
    }
  }, [soundNotificationsEnabled, settings]);

  const handleSoundNotificationToggle = (enabled: boolean) => {
    setSoundNotificationsEnabled(enabled);
  };

  const handleSave = () => {
    updateSettings(
      { soundNotificationsEnabled },
      {
        onSuccess: () => {
          setHasChanges(false);
          console.log('[SettingsView] Settings saved successfully');
        }
      }
    );
  };

  const handleCancel = () => {
    if (settings) {
      setSoundNotificationsEnabled(settings.soundNotificationsEnabled);
      setHasChanges(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          <p className="text-sm text-gray-600">Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Retour"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Paramètres
                </h1>
                <p className="text-sm text-gray-500">
                  Gérez vos préférences d'application
                </p>
              </div>
            </div>

            {hasChanges && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancel}
                  disabled={isUpdating}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XMarkIcon className="w-4 h-4" />
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={isUpdating}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      Enregistrer
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Notifications Section */}
          <div>
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
              Notifications
            </h2>
            <NotificationsSection
              soundNotificationsEnabled={soundNotificationsEnabled}
              onToggle={handleSoundNotificationToggle}
              isUpdating={isUpdating}
            />
          </div>

          {/* Future sections can be added here */}
          {/*
          <div>
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
              Apparence
            </h2>
            ...
          </div>
          */}
        </div>
      </div>

      {/* Unsaved changes warning */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-amber-50 to-orange-50 border-t-2 border-amber-300 shadow-lg py-4 px-4 sm:px-6 lg:px-8 z-20 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="p-2 bg-amber-100 rounded-full">
                  <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-900">Modifications non enregistrées</p>
                <p className="text-xs text-amber-700">Enregistrez ou annulez vos modifications</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCancel}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all transform hover:scale-105"
              >
                <XMarkIcon className="w-4 h-4" />
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={isUpdating}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 border-2 border-blue-600 rounded-lg hover:from-blue-700 hover:to-blue-800 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isUpdating ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-5 h-5" />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
